import React, { memo, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';

import SectionCardBase from './SectionCardBase';
import { floatToFixed } from '../../../../utils/number';
import palette from '../../../styles/palette';
import {
  formatMinutesToMMSS,
  formatSecondsToHHMM,
  fromMinutesToTime,
} from '../../../../utils/date';

const useStyles = makeStyles(() => ({
  label: {
    color: palette.typography.white.high,
    fontSize: '0.75rem',
    lineHeight: '1rem',
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'end',
    letterSpacing: '0rem',
  },
  smallValueMargin: {
    marginRight: '0.313rem !important',
    maxWidth: '3.438rem',
  },
  shiftMargin: {
    maxWidth: '100%',
    marginRight: '0.313rem !important',
    marginLeft: '-0.5rem !important',
    maxHeight: '1rem',
  },
  valueMargin: {
    marginRight: '0.875rem !important',
    minWidth: '3.813rem',
  },
  value: {
    color: palette.typography.white.medium,
    fontSize: '0.75rem',
    lineHeight: '1rem',
    letterSpacing: '-0.02rem',
    flex: 1,
    maxHeight: '1rem',
  },
  dataContainer: {
    display: 'flex',
  },
  container: {
    marginTop: '0.375rem',
    maxHeight: '6rem',
    flexWrap: 'wrap',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const SectionMinifiedCard = ({
  sectionStats: {
    totalRoutes,
    totalStops,
    totalParcels,
    totalCollections,
    totalParcelsOfd,
    totalExceptions,
    stopPerRoute,
    averageScanTime,
    averageDutyTime,
    averageOpportunityTime,
  },
  ...section
}) => {
  const styles = useStyles();
  const leftColumnStyle = [styles.label, styles.valueMargin];
  const rightColumnStyle = [styles.label, styles.smallValueMargin];

  const withZero = (value) => `${floatToFixed(value)}`.padStart(3, 0);
  const withPercentage = (percentage) =>
    !isNaN(percentage) && ` (${`${percentage}`.padStart(3, 0)}%)`;

  const sectionValuesConfig = useMemo(
    () => [
      {
        value: withZero(totalRoutes),
        subValue: '',
        label: 'Routes',
        style: leftColumnStyle,
      },
      {
        value: withZero(totalStops),
        subValue: '',
        label: 'Stops',
        style: leftColumnStyle,
      },
      {
        value: withZero(totalParcels),
        subValue: '',
        label: 'Parcels',
        style: leftColumnStyle,
      },
      {
        value: withZero(totalCollections),
        subValue: '',
        label: 'Collections',
        style: leftColumnStyle,
      },
      {
        value: withZero(totalExceptions),
        subValue: '',
        label: 'Exceptions',
        style: leftColumnStyle,
      },
      {
        value: withZero(section.routeStatus.departedTotal),
        subValue: withPercentage(section.routeStatus.departedPercentage),
        label: 'Departed',
        style: rightColumnStyle,
      },
      {
        value: withZero(stopPerRoute),
        subValue: '',
        label: 'SPR',
        style: [styles.label, styles.smallValueMargin],
      },
      {
        value: withZero(totalParcelsOfd),
        subValue: '',
        label: 'OFD',
        style: rightColumnStyle,
      },
      {
        value: formatMinutesToMMSS(averageScanTime),
        subValue: ' Mins',
        label: 'Scan Time',
        style: [styles.label, styles.shiftMargin],
      },
      {
        value: fromMinutesToTime(averageDutyTime || 0),
        subValue: '',
        label: 'Duty',
        style: rightColumnStyle,
      },
      {
        value: formatSecondsToHHMM(averageOpportunityTime || 0),
        subValue: '',
        label: 'OT',
        style: rightColumnStyle,
      },
    ],
    []
  );

  const renderRow = useCallback(
    ({ label, value, style, subValue }) => (
      <Box className={styles.dataContainer} key={`${label}-label`}>
        <Typography className={clsx(style)} data-testid={`${label}-label`}>
          {label}
        </Typography>
        <Typography className={styles.value} data-testid={`${label}-value`}>
          {value}
          {subValue}
        </Typography>
      </Box>
    ),
    []
  );

  return (
    <SectionCardBase {...section}>
      <Box className={styles.container}>
        {sectionValuesConfig.map((item) => renderRow(item))}
      </Box>
    </SectionCardBase>
  );
};

export default memo(SectionMinifiedCard);
